/**
 * Location for check-in/out: accurate, without making the employee wait.
 *
 * A browser's first position is often a rough estimate (Wi-Fi or IP based,
 * sometimes kilometres off) that it then refines. So instead of taking the
 * first answer, we *watch* the position for a few seconds and keep the most
 * accurate reading, stopping early once it is good (≤ 30 m). Readings are
 * always new (maximumAge 0) — never a cached, possibly old position.
 *
 * This runs ahead of time: once the browser has permission, the app keeps a
 * recent accurate reading in the background, so a click uses it instantly.
 * Without one, the click waits (the button has already flipped) for a new
 * reading of up to ~3.5 s, and otherwise continues without a location.
 */
export interface Fix { latitude: number; longitude: number; accuracy: number }

/** A primed reading is used for a click only when this recent… */
const FRESH_MS = 90 * 1000;
/** …and at least this accurate (metres). */
const USABLE_M = 100;
/** Stop watching as soon as a reading is this accurate (metres). */
const GOOD_M = 30;

let last: { fix: Fix; at: number } | null = null;
let inflight: Promise<Fix | null> | null = null;

/**
 * Watches the position for up to `maxMs` and resolves with the most accurate
 * reading seen (early when one is ≤ GOOD_M), or null when none arrived.
 */
function bestFix(maxMs: number): Promise<Fix | null> {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return Promise.resolve(null);
    return new Promise((resolve) => {
        let best: Fix | null = null;
        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            navigator.geolocation.clearWatch(id);
            clearTimeout(timer);
            if (best) last = { fix: best, at: Date.now() };
            resolve(best);
        };
        const id = navigator.geolocation.watchPosition(
            (p) => {
                const fix = { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy };
                if (!best || fix.accuracy < best.accuracy) {
                    best = fix;
                    // Publish each improved reading immediately so a click can use a
                    // partially-complete priming run instead of waiting for the full
                    // timeout (laptops rarely reach the ≤30 m early-stop threshold).
                    last = { fix: best, at: Date.now() };
                }
                if (fix.accuracy <= GOOD_M) finish();
            },
            (err) => { if (err.code === err.PERMISSION_DENIED) finish(); },
            { enableHighAccuracy: true, maximumAge: 0, timeout: maxMs },
        );
        const timer = setTimeout(finish, maxMs);
    });
}

async function permissionState(): Promise<PermissionState | 'unknown'> {
    try {
        return (await navigator.permissions?.query({ name: 'geolocation' as PermissionName }))?.state ?? 'unknown';
    } catch {
        return 'unknown';
    }
}

/** Background reading, only when permission is already granted (never pops a prompt by itself). */
export async function primeLocation() {
    if (inflight || (last && Date.now() - last.at < FRESH_MS / 2 && last.fix.accuracy <= GOOD_M)) return;
    if ((await permissionState()) !== 'granted') return;
    inflight = bestFix(15_000).finally(() => { inflight = null; });
}

let timer: ReturnType<typeof setInterval> | null = null;
/** Keeps a recent accurate reading while the app is open. Safe to call many times. */
export function startLocationPriming() {
    primeLocation();
    if (timer) return;
    timer = setInterval(() => { if (document.visibilityState === 'visible') primeLocation(); }, 60 * 1000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') primeLocation(); });
}

/** A location for a click: instant when a recent accurate reading exists, else a new best reading (≤ ~6 s), else null. */
export async function getFastLocation(): Promise<Fix | null> {
    const usable = (f: { fix: Fix; at: number } | null) => !!f && Date.now() - f.at < FRESH_MS && f.fix.accuracy <= USABLE_M;
    if (usable(last)) return last!.fix;
    // Permission already granted: cap the click wait short — we take the best
    // reading seen within the window rather than hanging for a precise fix.
    // First use still allows time for the person to answer the permission prompt.
    const wait = (await permissionState()) === 'granted' ? 3500 : 15_000;
    const fix = inflight
        ? await Promise.race([inflight, new Promise<null>((r) => setTimeout(() => r(null), wait))])
        : await bestFix(wait);
    return fix ?? (last && Date.now() - last.at < FRESH_MS ? last.fix : null);
}
